import { add, min, multiplyWithFactor, multiplyWithPercent, multiplyWithRate, round, sum, } from "@pentacode/openapi";
import { calculateBreakDurations } from "../breaks";
import { getSalaryOrDefault } from "../contract.util";
import { AbsenceMode, BonusTypeMode, BreakTiming, TimeEntryType, } from "../model";
import { getAncillaryCostFactor, getHourlyRate } from "../payroll";
import { getContractForDate, getCostCenter, getTimeSettingsForEntry, } from "../utils";
import { getBonusResults } from "./bonus-result";
import { hour } from "./consts";
import { getIntervalDuration } from "./interval";
function calculateMealCost(meal) {
    const costs = (meal && multiplyWithRate(meal.count, meal.value)) || 0;
    return meal
        ? {
            costs,
            ...meal,
        }
        : undefined;
}
export function makeMealResult(meals) {
    return {
        breakfast: calculateMealCost(meals.breakfast),
        lunch: calculateMealCost(meals.lunch),
        dinner: calculateMealCost(meals.dinner),
    };
}
export function sumMeals(meals) {
    return sum(meals.breakfast?.costs || 0, meals.lunch?.costs || 0, meals.dinner?.costs || 0);
}
export function makeEmptyTimeResult() {
    return {
        base: {
            duration: 0,
            hourlyRate: 0,
            wages: 0,
            ancillaryCosts: 0,
        },
        breaks: {
            duration: 0,
            paidDuration: 0,
            hourlyRate: 0,
            wages: 0,
            ancillaryCosts: 0,
        },
        bonuses: [],
        meals: {},
        days: 0,
        totalWages: 0,
        totalCosts: 0,
    };
}
//TODO: here, we calculate wages with high precision, should we adapt to bonus calculation?
export function calculateBaseTimeResult(duration, hourlyRate, ancillaryCostFactor) {
    const wages = multiplyWithRate(duration, hourlyRate);
    const ancillaryCosts = multiplyWithFactor(wages, ancillaryCostFactor);
    return {
        duration,
        hourlyRate,
        wages,
        ancillaryCosts,
    };
}
export function calculateBreakTimeResult(breakPaid, breakTotal, hourlyRate, ancillaryCostFactor) {
    const wages = multiplyWithRate(breakPaid, hourlyRate);
    const ancillaryCosts = multiplyWithFactor(wages, ancillaryCostFactor);
    return {
        duration: breakTotal,
        paidDuration: breakPaid,
        hourlyRate,
        wages,
        ancillaryCosts,
    };
}
function makeHourAdjustmentResult(input) {
    const base = calculateBaseTimeResult(input.baseDuration, input.hourlyRate, 0 // No ancillary costs for hour adjustments
    );
    const breaks = calculateBreakTimeResult(0, // No breaks for hour adjustments
    0, // No breaks for hour adjustments
    input.hourlyRate, 0 // No ancillary costs for hour adjustments
    );
    return {
        base,
        breaks,
        bonuses: [],
        meals: {},
        days: 0,
        totalWages: base.wages,
        totalCosts: base.wages,
    };
}
export function makeWorkTimeResult(input) {
    const base = calculateBaseTimeResult(input.baseDuration, input.hourlyRate, input.ancillaryCostFactor);
    const breaks = calculateBreakTimeResult(input.breakPaid, input.breakTotal, input.hourlyRate, input.ancillaryCostFactor);
    const bonusWages = input.bonuses.reduce((total, bonus) => add(total, bonus.wages), 0);
    const bonusAncillaryCosts = input.bonuses.reduce((total, bonus) => add(total, bonus.ancillaryCosts), 0);
    const commissionWages = (input.revenue && input.commission && multiplyWithPercent(input.revenue, input.commission)) || 0;
    const commissionCost = multiplyWithFactor(commissionWages, input.ancillaryCostFactor);
    const mealsCost = makeMealResult(input.meals);
    const totalMealsCost = sumMeals(mealsCost);
    const totalWages = sum(base.wages, breaks.wages, bonusWages, commissionWages);
    const totalCosts = sum(totalWages, base.ancillaryCosts, breaks.ancillaryCosts, bonusAncillaryCosts, totalMealsCost, commissionCost);
    return {
        meals: mealsCost,
        base,
        breaks,
        commission: commissionWages &&
            input.revenue && // TODO: input.revenue is checked twice
            input.commission &&
            input.revenue && {
            revenue: input.revenue,
            percent: input.commission,
            wages: commissionWages,
            ancillaryCosts: commissionCost,
        },
        bonuses: input.bonuses,
        days: input.days,
        totalWages,
        totalCosts,
        costCenter: input.costCenter
            ? {
                number: input.costCenter.number,
                name: input.costCenter.name,
            }
            : undefined,
    };
}
export function makeAbsenceTimeResult(input) {
    const base = calculateBaseTimeResult(input.baseDuration, input.hourlyRate, input.ancillaryCostFactor);
    const breaks = calculateBreakTimeResult(input.breakPaid, input.breakTotal, input.hourlyRate, input.ancillaryCostFactor);
    const bonusWages = input.bonuses.reduce((total, bonus) => add(total, bonus.wages), 0);
    const bonusAncillaryCosts = input.bonuses.reduce((total, bonus) => add(total, bonus.ancillaryCosts), 0);
    const totalWages = sum(base.wages, breaks.wages, bonusWages);
    const totalCosts = sum(totalWages, base.ancillaryCosts, breaks.ancillaryCosts, bonusAncillaryCosts);
    return {
        base,
        breaks,
        bonuses: input.bonuses,
        days: input.days,
        totalWages,
        totalCosts,
        costCenter: input.costCenter
            ? {
                number: input.costCenter.number,
                name: input.costCenter.name,
            }
            : undefined,
        meals: {},
    };
}
function makeVacationAdjustmentResult(input) {
    const base = calculateBaseTimeResult(input.baseDuration, input.hourlyRate, 0 // No ancillary costs for vacation adjustments
    );
    const breaks = calculateBreakTimeResult(input.breakPaid, input.breakTotal, input.hourlyRate, 0 // No ancillary costs for vacation adjustments
    );
    const totalWages = sum(base.wages, breaks.wages);
    const totalCosts = sum(totalWages);
    return {
        base,
        breaks,
        bonuses: [],
        meals: {},
        days: input.days,
        totalWages,
        totalCosts,
    };
}
export function makeTimeResult({ baseDuration, breakPaid = 0, breakTotal = 0, revenue, commission, bonuses = [], days = 0, meals = {}, hourlyRate, ancillaryCostFactor, costCenter, }) {
    const base = calculateBaseTimeResult(baseDuration, hourlyRate, ancillaryCostFactor);
    const breaks = calculateBreakTimeResult(breakPaid, breakTotal, hourlyRate, ancillaryCostFactor);
    const bonusWages = bonuses.reduce((total, bonus) => add(total, bonus.wages), 0);
    const bonusAncillaryCosts = bonuses.reduce((total, bonus) => add(total, bonus.ancillaryCosts), 0);
    const commissionWages = (revenue && commission && multiplyWithPercent(revenue, commission)) || 0;
    const commissionCost = multiplyWithFactor(commissionWages, ancillaryCostFactor);
    const mealsResult = makeMealResult(meals);
    const totalMealsCost = sumMeals(mealsResult);
    const totalWages = sum(base.wages, breaks.wages, bonusWages, commissionWages, commissionWages);
    const totalCosts = sum(totalWages, base.ancillaryCosts, breaks.ancillaryCosts, bonusAncillaryCosts, totalMealsCost, commissionCost);
    return {
        meals: mealsResult,
        base,
        breaks,
        commission: commissionWages &&
            revenue &&
            commission &&
            revenue && {
            revenue,
            percent: commission,
            wages: commissionWages,
            ancillaryCosts: commissionCost,
        },
        bonuses,
        days,
        totalWages,
        totalCosts,
        costCenter: costCenter
            ? {
                number: costCenter.number,
                name: costCenter.name,
            }
            : undefined,
    };
}
export function addMealCosts(a, b, factor) {
    if (!a && !b) {
        return undefined;
    }
    // TODO: "value" doesn't seem to make much sense here
    return {
        count: add(a?.count, b?.count),
        value: multiplyWithFactor(add(a?.value, b?.value), factor), // TODO: this should likely be 0.5 and factor should not be a parameter
        costs: add(a?.costs, b?.costs),
    };
}
export function addBonusResults(bonusesA, bonusesB) {
    const bonuses = bonusesA.map((bonus) => ({ ...bonus }));
    for (const bonus of bonusesB) {
        let existing = bonuses.find((bon) => bon.bonusId === bonus.bonusId && bon.hourlyRate === bonus.hourlyRate && bon.taxFree === bonus.taxFree);
        if (!existing) {
            existing = { ...bonus, duration: 0, wages: 0, ancillaryCosts: 0 };
            bonuses.push(existing);
        }
        existing.duration = add(existing.duration, bonus.duration);
        existing.wages = add(existing.wages, bonus.wages);
        existing.ancillaryCosts = add(existing.ancillaryCosts, bonus.ancillaryCosts);
    }
    return bonuses;
}
export function addTimeResults(a, b) {
    return {
        base: {
            duration: add(a.base.duration, b.base.duration),
            hourlyRate: add(a.base.hourlyRate, b.base.hourlyRate),
            wages: add(a.base.wages, b.base.wages),
            ancillaryCosts: add(a.base.ancillaryCosts, b.base.ancillaryCosts),
        },
        breaks: {
            duration: add(a.breaks.duration, b.breaks.duration),
            paidDuration: add(a.breaks.paidDuration, b.breaks.paidDuration),
            hourlyRate: add(a.breaks.hourlyRate, b.breaks.hourlyRate),
            wages: add(a.breaks.wages, b.breaks.wages),
            ancillaryCosts: add(a.breaks.ancillaryCosts, b.breaks.ancillaryCosts),
        },
        bonuses: addBonusResults(a.bonuses ?? [], b.bonuses ?? []), // TODO: type determines we shouldn't need defaults?
        meals: {
            breakfast: addMealCosts(a.meals.breakfast, b.meals.breakfast, 0.5),
            lunch: addMealCosts(a.meals.lunch, b.meals.lunch, 2), // TODO: this is likely a bug and should be 0.5 as well
            dinner: addMealCosts(a.meals.dinner, b.meals.dinner, 2), // TODO: this is likely a bug and should be 0.5 as well
        },
        days: add(a.days, b.days),
        totalWages: add(a.totalWages, b.totalWages),
        totalCosts: add(a.totalCosts, b.totalCosts),
    };
}
export function getTotalResults(results) {
    return results.reduce((total, res) => addTimeResults(total, res), makeEmptyTimeResult());
}
export function getDailyAverage(results) {
    const total = getTotalResults(results);
    const average = makeEmptyTimeResult();
    const days = total.days;
    if (!days) {
        return total;
    }
    const dayFraction = (1 / days);
    average.base.duration = multiplyWithFactor(total.base.duration, dayFraction);
    average.base.wages = multiplyWithFactor(total.base.wages, dayFraction);
    average.base.ancillaryCosts = multiplyWithFactor(total.base.ancillaryCosts, dayFraction);
    average.base.hourlyRate = multiplyWithFactor(total.base.hourlyRate, dayFraction);
    const bonusesByType = results.reduce((bbt, result) => {
        if (!result) {
            return bbt;
        }
        for (const bonus of result.bonuses || []) {
            if (!bbt.has(bonus.type.id)) {
                bbt.set(bonus.type.id, []);
            }
            bbt.get(bonus.type.id).push(bonus);
        }
        return bbt;
    }, new Map());
    average.bonuses = [...bonusesByType.values()].map((bonuses) => ({
        ...bonuses[0],
        duration: multiplyWithFactor(bonuses.reduce((total, bonus) => add(total, bonus.duration), 0), dayFraction),
        wages: multiplyWithFactor(bonuses.reduce((total, bonus) => add(total, bonus.wages), 0), dayFraction),
        ancillaryCosts: multiplyWithFactor(bonuses.reduce((total, bonus) => add(total, bonus.ancillaryCosts), 0), dayFraction),
        hourlyRate: multiplyWithFactor(bonuses.reduce((total, bonus) => add(total, bonus.hourlyRate), 0), dayFraction),
    }));
    average.meals = {
        breakfast: total.meals.breakfast && {
            count: multiplyWithFactor(total.meals.breakfast.count, dayFraction),
            value: multiplyWithFactor(total.meals.breakfast.value, dayFraction),
            costs: multiplyWithFactor(total.meals.breakfast.costs, dayFraction),
        },
        lunch: total.meals.lunch && {
            count: multiplyWithFactor(total.meals.lunch.count, dayFraction),
            value: multiplyWithFactor(total.meals.lunch.value, dayFraction),
            costs: multiplyWithFactor(total.meals.lunch.costs, dayFraction),
        },
        dinner: total.meals.dinner && {
            count: multiplyWithFactor(total.meals.dinner.count, dayFraction),
            value: multiplyWithFactor(total.meals.dinner.value, dayFraction),
            costs: multiplyWithFactor(total.meals.dinner.costs, dayFraction),
        },
    };
    return average;
}
/** Gets the shift duration for calculating the time result. */
export function getShiftInterval(entry, mode) {
    switch (mode) {
        case "planned":
            return entry.planned;
        case "logged":
            return entry.logged;
        case "final":
            return entry.final;
        case "mixed":
            if (entry.final || entry.isPast) {
                return entry.final;
            }
            return entry.planned;
        case "max": {
            if (!entry.logged || !entry.final) {
                return entry.final;
            }
            // if the two views of the same shift(logged and final) don't share a single minute,
            // they can't actually be the same worked shift,
            // that only happens when the shift was moved to another date and the logged stayed behind
            // so the shift is ommited from the savings calculations
            if (entry.logged[1].getTime() < entry.final[0].getTime() ||
                entry.logged[0].getTime() > entry.final[1].getTime()) {
                return entry.final;
            }
            return [
                new Date(Math.min(entry.logged[0].getTime(), entry.final[0].getTime())),
                new Date(Math.max(entry.logged[1].getTime(), entry.final[1].getTime())),
            ];
        }
    }
}
export function getTimeResult(settings, company, employee, entry, prevAverage, shiftsThatDay = 1, mode = "final") {
    const timeSettings = getTimeSettingsForEntry(settings.availableTimeSettings, company, entry);
    const { iterativeBreaks } = settings;
    const mealValueBreakfast = settings.mealValueBreakfast;
    const mealValueLunch = settings.mealValueLunch;
    const mealValueDinner = settings.mealValueDinner;
    const contract = getContractForDate(employee.contracts, entry.date);
    const { paidBreaksAuto, paidBreaksManual, breakTiming } = timeSettings;
    const costCenter = getCostCenter(company.costCenters, { company, employee, timeEntry: entry, date: entry.date });
    if (!contract) {
        return makeEmptyTimeResult();
    }
    const hourlyRate = getHourlyRate(settings, company.benefitTypes, contract, entry.positionId);
    const salary = getSalaryOrDefault(entry.positionId, contract.salaries);
    const commission = salary?.commission;
    const ancillaryCostFactor = getAncillaryCostFactor(contract.employmentType)[0];
    const { absenceMode, absenceHours, fixedWorkDays, hoursPerDay } = contract;
    if (entry.type === TimeEntryType.Work) {
        const shiftinterval = getShiftInterval(entry, mode);
        if (!shiftinterval || !shiftinterval[0] || !shiftinterval[1]) {
            return makeEmptyTimeResult();
        }
        const shiftDuration = getIntervalDuration(shiftinterval);
        const breakDurations = calculateBreakDurations(entry, timeSettings, mode, shiftDuration, iterativeBreaks);
        const breakPaid = add(paidBreaksAuto ? breakDurations.auto : 0, paidBreaksManual ? breakDurations.manual : 0);
        const breakTotal = add(breakDurations.auto, breakDurations.manual);
        let [start, end] = shiftinterval;
        // subtract break at start of end of shift, depending on setting
        if (breakTiming === BreakTiming.End) {
            end = new Date(end.getTime() - breakTotal * hour);
        }
        else {
            start = new Date(start.getTime() + breakTotal * hour);
        }
        const work = [start, end];
        const baseDuration = ((end.getTime() - start.getTime()) / hour);
        const bonuses = getBonusResults(settings, company, employee, entry, work);
        return makeWorkTimeResult({
            breakPaid,
            breakTotal,
            baseDuration,
            bonuses,
            meals: {
                breakfast: entry.mealsBreakfast
                    ? {
                        count: entry.mealsBreakfast,
                        value: mealValueBreakfast || 0,
                    }
                    : undefined,
                lunch: entry.mealsLunch
                    ? {
                        count: entry.mealsLunch,
                        value: mealValueLunch || 0,
                    }
                    : undefined,
                dinner: entry.mealsDinner
                    ? {
                        count: entry.mealsDinner,
                        value: mealValueDinner || 0,
                    }
                    : undefined,
            },
            commission,
            revenue: entry.revenue,
            hourlyRate,
            ancillaryCostFactor,
            days: (1 / shiftsThatDay),
            costCenter,
        });
    }
    // During absences
    if ([
        TimeEntryType.Vacation,
        TimeEntryType.Sick,
        TimeEntryType.ChildSick,
        TimeEntryType.SickInKUG,
        TimeEntryType.CompDay,
    ].includes(entry.type)) {
        let baseDuration = prevAverage.base.duration;
        let breakTotal = prevAverage.breaks.duration;
        let breakPaid = prevAverage.breaks.paidDuration;
        if (absenceMode === AbsenceMode.FixedDays && fixedWorkDays) {
            baseDuration = hoursPerDay ? hoursPerDay[(entry.start.getDay() + 6) % 7] : 0;
            breakTotal = breakPaid = 0;
        }
        else if (absenceMode === AbsenceMode.Fixed) {
            baseDuration = absenceHours;
            breakTotal = breakPaid = 0;
        }
        const bonuses = [];
        if (entry.type !== TimeEntryType.CompDay) {
            // Add Bonus based on the 13-week average for each Entry Absence type except CompDay
            // TODO: The logic for work bonuses is in bonus-result.ts, we should move this logic there as well.
            for (const bonus of contract.bonuses) {
                const bonusType = company.bonusTypes?.find((t) => t.id === bonus.typeId);
                if (bonusType?.continuedPay && bonusType?.matchesDate(entry.date, company.country)) {
                    const prevBonusAverage = prevAverage.bonuses?.find((b) => b.type.id === bonus.typeId);
                    const applicableDuration = min(baseDuration, prevBonusAverage?.duration || 0);
                    const duration = calculateBonusDuration(new Date(entry.date), bonusType.mode, applicableDuration, baseDuration);
                    const wages = multiplyWithPercent(multiplyWithRate(round(duration, 2), hourlyRate), bonus.percent);
                    bonuses.push({
                        bonusId: bonus.id,
                        type: {
                            id: bonusType.id,
                            name: bonusType.name,
                            legalType: bonusType.legalType,
                        },
                        duration, // We always use the full duration and don't split into taxed and tax-free parts, like we would do for work bonuses.
                        taxFree: false, // Absence bonuses are always fully taxed.
                        percent: bonus.percent,
                        hourlyRate,
                        wages,
                        ancillaryCosts: multiplyWithFactor(wages, ancillaryCostFactor),
                    });
                }
            }
        }
        return makeAbsenceTimeResult({
            baseDuration: multiplyWithFactor(baseDuration, (entry.days || 1)),
            breakPaid,
            breakTotal,
            bonuses,
            hourlyRate,
            ancillaryCostFactor,
            days: entry.days || 1,
            costCenter,
        });
    }
    if (entry.type === TimeEntryType.HourAdjustment) {
        return makeHourAdjustmentResult({
            baseDuration: entry.hours ?? 0,
            hourlyRate: entry.paid ? hourlyRate : 0,
        });
    }
    if (entry.type === TimeEntryType.VacationAdjustment) {
        const days = (entry.days || 0);
        let baseDuration = prevAverage.base.duration;
        let breakTotal = prevAverage.breaks.duration;
        let breakPaid = prevAverage.breaks.paidDuration;
        if (absenceMode === AbsenceMode.FixedDays && fixedWorkDays) {
            baseDuration = hoursPerDay ? hoursPerDay[(entry.start.getDay() + 6) % 7] : 0;
            breakTotal = breakPaid = 0;
        }
        else if (absenceMode === AbsenceMode.Fixed) {
            baseDuration = absenceHours;
            breakTotal = breakPaid = 0;
        }
        return makeVacationAdjustmentResult({
            baseDuration: multiplyWithFactor(baseDuration, days),
            hourlyRate: entry.paid ? hourlyRate : 0,
            breakTotal: multiplyWithFactor(breakTotal, days),
            breakPaid: multiplyWithFactor(breakPaid, days),
            days: entry.days || 0,
        });
    }
    return makeEmptyTimeResult();
}
export function calculateBonusDuration(date, bonusTypeMode, applicableDuration, baseDuration) {
    if (bonusTypeMode === BonusTypeMode.Daily) {
        // always use the 13-week average for daily bonuses
        return applicableDuration;
    }
    else if (bonusTypeMode === BonusTypeMode.WeekDays && date.getDay() !== 0) {
        // for week day bonuses (except sundays) use the 13-week average
        return applicableDuration;
    }
    // use base duration for sundays, holidays and single dates (like 24.12.)
    return baseDuration;
}
/** MUTATES THE TIMEENTRY - calculates and sets result, resultPlanned and resultMaxCost if not set yet */
export function fillTimeEntryResults(entry, settings, company, employee, workEntriesThatDay, prevAverage) {
    if (!entry.result) {
        entry.result = getTimeResult(settings, company, employee, entry, prevAverage || makeEmptyTimeResult(), workEntriesThatDay.filter((e) => e.startFinal && e.endFinal).length, "final");
    }
    if (!entry.resultPlanned) {
        entry.resultPlanned = getTimeResult(settings, company, employee, entry, prevAverage || makeEmptyTimeResult(), workEntriesThatDay.length, "planned");
    }
    if (!entry.resultMaxTotalCosts) {
        const { totalCosts } = getTimeResult(settings, company, employee, entry, prevAverage || makeEmptyTimeResult(), workEntriesThatDay.length, "max");
        entry.resultMaxTotalCosts = totalCosts;
    }
}
//# sourceMappingURL=time-result.js.map